import React from 'react';
import moment from 'moment';
import i18n from 'i18next';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Card, ContentCard, Dropdown, DropdownItem, Filter, FilterSetController, Icon, Status, Table, Tab, Tooltip, ToastContext, StopPropagation } from '@weezevent/nacre';
import { ModerationOperators, ModerationFilesManagement } from '../../../../models';
import { importFilesStatusType, importStatusFilterOptions } from '../../../../constants/moderation';
import { handleSearchQuery } from '../../../manager/components/context';
import { useCurrentPageFilters } from '../../../../utils/filters';
import { DateInTable } from '../../components/DateInTable';
import LoadingTable from '../../../../components/loadingTable';
import { InfoTextTooltip } from '../../../attendee/detail/components/infotext-tooltip/infoTextTooltip';
import ModerationPaginator from '../../components/moderationPaginator';
import useModerationContext from '../../../../context/useModerationContext';

import Styles from './filesManagement.module.css';

const spec = {
    multiple: ['status'],
    fields: ['search'],
    aliases: {},
    date: ['uploaded_at'],
    form: []
};

export default function ImportsTab({ organization, flowId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [, setModerationContext] = useModerationContext();
    const [currentPage, setCurrentPage] = React.useState(0);
    const [pageSize] = React.useState(50);
    const [filters, setFilters] = useCurrentPageFilters(spec);
    const defaultFilters = {};
    const locale = i18n.language;
    const tz = moment.tz.guess();
    const prefix = React.useMemo(() => `/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/files`, [flowId, organization]);

    const [operators, loadingOperators] = ModerationOperators.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            launch: flowId
        },
        [organization, flowId]
    );

    const [files, loadingFiles, syncFiles] = ModerationFilesManagement.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            launch: flowId,
            upload: true,
            cache: false,
            allow_cache: false,
            query: {
                offset: currentPage * pageSize,
                limit: pageSize,
                ...handleSearchQuery(spec, filters)
            },
            onSuccess: () => {
                setModerationContext({ syncActiveTab: syncFiles, download_all_enable: false });
            }
        },
        [organization, flowId, filters, pageSize, currentPage]
    );

    const operatorsOptions = React.useMemo(() => {
        if (!operators || loadingOperators) {
            return [];
        }

        return operators.map(op => ({
            ...op.toOptions(),
            text: <InfoTextTooltip title={`${op.first_name} ${op.last_name}`} subtitle={op.email} tooltip={false} />
        }));
    }, [operators, loadingOperators]);

    const handleSearch = React.useCallback(
        value => {
            setFilters({ ...filters, search: value });
        },
        [filters]
    );

    const handleDownloadFile = React.useCallback(url => {
        ModerationFilesManagement.downloadFile({
            url
        })
            .then(res => {
                const blob = new Blob([res.response], { type: 'text/csv' });
                const file = window.URL.createObjectURL(blob);
                window.open(file, '_blank');
            })
            .catch(e => {
                toast.error(t('ticket.moderation.filesManagement.downloadError'));
            });
    }, []);

    return (
        <Tab
            panes={[
                {
                    id: 0,
                    menuItem: t('ticket.moderation.filesManagement.exportsTab'),
                    menuIcon: 'File-export',
                    url: `${prefix}/exports`
                },
                {
                    id: 1,
                    menuItem: t('ticket.moderation.filesManagement.importsTab'),
                    menuIcon: 'upload',
                    url: `${prefix}/imports`
                }
            ]}
            activeTab={1}
            onTabSelected={tab => {
                setFilters(defaultFilters);
                history.push(tab.url);
            }}
        >
            <Card>
                <ContentCard>
                    <FilterSetController
                        total={files?.__meta?.headers?.get('total-count') || 0}
                        labelTotal={t('ticket.moderation.filesManagement.table.countTotal', { count: files?.__meta?.headers?.get('total-count') || 0 })}
                        onChange={values => setFilters(values)}
                        initialValues={filters}
                        defaultValues={defaultFilters}
                        onReset={() => {}}
                        search={{
                            onChange: (_, search = '') => handleSearch(search),
                            handleReset: () => setFilters(filters => ({ ...filters, search: '' })),
                            placeholder: t('ticket.moderation.common.searchPlaceholder'),
                            value: filters?.search
                        }}
                    >
                        <Filter name="status" labelName={t('ticket.moderation.filesManagement.table.filterStatus')} multiple options={importStatusFilterOptions()} index="1" />
                        <Filter name="operator" labelName={t('ticket.moderation.filesManagement.table.filterOperator')} multiple options={operatorsOptions} index="2" />
                        <Filter
                            date
                            index="3"
                            name="uploaded_at"
                            locale={locale}
                            timeStep={5}
                            timezone={tz}
                            labelCTA={t('ticket.common.action.apply')}
                            labelName={t('ticket.moderation.filesManagement.table.filterDate')}
                            labelAfter={t('ticket.manager.filters.date.after')}
                            labelBefore={t('ticket.manager.filters.date.before')}
                            labelBetween={t('ticket.manager.filters.date.between')}
                            labelSelect={t('ticket.manager.filters.date.select')}
                            labelDate={t('ticket.manager.filters.date.date')}
                            labelHour={t('ticket.manager.filters.date.hour')}
                            labelStartDate={t('ticket.manager.filters.date.start_date')}
                            labelStartHour={t('ticket.manager.filters.date.start_hour')}
                            labelEndDate={t('ticket.manager.filters.date.end_date')}
                            labelEndHour={t('ticket.manager.filters.date.end_hour')}
                        />
                    </FilterSetController>
                </ContentCard>
                <Table useNew>
                    <Table.Header>
                        <Table.Tr>
                            <th>{t('ticket.moderation.filesManagement.table.date')}</th>
                            <th>{t('ticket.moderation.filesManagement.table.user')}</th>
                            <th>{t('ticket.moderation.filesManagement.table.validated')}</th>
                            <th>{t('ticket.moderation.filesManagement.table.ignored')}</th>
                            <th>{t('ticket.moderation.filesManagement.table.error')}</th>
                            <th>{t('ticket.moderation.filesManagement.table.total')}</th>
                            <th className={Styles['import-status-column-header']}>{t('ticket.moderation.filesManagement.table.status')}</th>
                            <th>{t('ticket.moderation.filesManagement.table.actions')}</th>
                        </Table.Tr>
                    </Table.Header>
                    {!!files?.length && !loadingFiles && (
                        <Table.TBody>
                            {files
                                ?.sort((b, a) => a.created?.localeCompare(b.created))
                                .map(file => (
                                    <FilesRow key={file.id} file={file} organization={organization} handleDownload={handleDownloadFile} flowId={flowId} />
                                ))}
                        </Table.TBody>
                    )}
                </Table>
                <LoadingTable loading={loadingFiles} height="30vh" />
                {!files?.length && !loadingFiles && <Card.EmptyState title={t('ticket.moderation.filesManagement.emptyTitle')} icon={<Icon name="list" />} />}
                <ModerationPaginator currentPage={currentPage} setCurrentPage={setCurrentPage} totalCount={+files?.__meta?.headers?.get('total-count') || 0} pageSize={pageSize} />
            </Card>
        </Tab>
    );
}

const FilesRow = ({ file, organization, handleDownload, flowId }) => {
    const { t } = useTranslation();
    const history = useHistory();

    return (
        <Table.Tr onClick={() => history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/files/imports/${file.id}`)}>
            <Table.Td>
                <DateInTable date={file.created} timezone={organization.timezone} language={organization.default_locale} />
            </Table.Td>
            <Table.Td>
                <InfoTextTooltip title={file.filename} subtitle={`${file.operator.first_name} ${file.operator.last_name}`} tooltip={false} />
            </Table.Td>
            <Table.Td>
                <p>{file.stats.validated}</p>
            </Table.Td>
            <Table.Td>
                <p>{file.stats.ignored}</p>
            </Table.Td>
            <Table.Td>
                <p>{file.stats.error}</p>
            </Table.Td>
            <Table.Td>
                <p>{file.stats.total}</p>
            </Table.Td>
            <Table.Td>
                <Tooltip title={t(`ticket.moderation.common.status.tooltip.${file.status}`)} triggerOnHover>
                    <Status color={importFilesStatusType[file.status]} text={t(`ticket.moderation.common.status.${file.status}`)} />
                </Tooltip>
            </Table.Td>
            <Table.Td>
                <Dropdown closeOnDocumentClick size="medium">
                    <StopPropagation>
                        <DropdownItem item={t('ticket.moderation.filesManagement.table.actionDownloadFile')} disabled={!file.url} onClick={() => handleDownload(file.url)} />
                    </StopPropagation>
                </Dropdown>
            </Table.Td>
        </Table.Tr>
    );
};
